/**
 * Document Detail/View Page
 *
 * Displays detailed information about a specific document:
 * - Document metadata and status
 * - Version history
 * - Verification status
 * - Document preview (mock in Sprint 1)
 * - Actions (download, update version, delete)
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { Table } from '../../components/common/Table';
import { Alert } from '../../components/common/Alert';
import { mockApi } from '../../services/mockApi';
import { formatFileSize, formatDate, formatDateTime } from '../../utils/formatting';
import type { Document, DocumentVersion, DocumentStatus } from '../../data/documents';

export const DocumentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(['common', 'company']);
  const [loading, setLoading] = useState(true);
  const [document, setDocument] = useState<Document | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDocument();
  }, [id]);

  const loadDocument = async () => {
    if (!id) {
      setError('Document ID not provided');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await mockApi.getDocument(id);
      if (response.success && response.data) {
        setDocument(response.data);
      } else {
        setError(response.error || 'Document not found');
      }
    } catch (err) {
      console.error('Error loading document:', err);
      setError('Failed to load document');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: DocumentStatus) => {
    const statusMap: Record<DocumentStatus, { variant: 'default' | 'success' | 'warning' | 'error' | 'info'; label: string }> = {
      DRAFT: { variant: 'default', label: t('common:status.draft') },
      ACTIVE: { variant: 'success', label: t('common:status.active') },
      ARCHIVED: { variant: 'warning', label: 'Archived' },
      EXPIRED: { variant: 'error', label: t('common:status.expired') },
      PENDING_REVIEW: { variant: 'info', label: 'Pending Review' },
    };
    const config = statusMap[status];
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const isExpiringSoon = (doc: Document): boolean => {
    if (!doc.expiryDate) return false;
    const daysUntilExpiry = Math.ceil(
      (new Date(doc.expiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    );
    return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
  };

  const handleDownload = (version?: string) => {
    const versionText = version ? ` version ${version}` : '';
    alert(`Download${versionText}: ${document?.name}`);
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete "${document?.name}"? This action cannot be undone.`)) {
      alert('Document deleted (mock action)');
      // Navigate back to document vault
    }
  };

  const handleUploadNewVersion = () => {
    alert('Upload new version (navigate to upload page with document ID)');
  };

  if (loading) {
    return (
      <PageLayout pageTitle="Loading...">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">{t('common:status.loading')}...</div>
        </div>
      </PageLayout>
    );
  }

  if (error || !document) {
    return (
      <PageLayout pageTitle="Document Not Found">
        <Alert type="error" title="Error">
          {error || 'Document not found'}
        </Alert>
        <div className="mt-4">
          <Button variant="outline" onClick={() => alert('Navigate to Document Vault')}>
            ← Back to Documents
          </Button>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      pageTitle={document.name}
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('company:dashboard.title'), href: '/company/dashboard' },
        { label: t('company:documents.title'), href: '/company/documents' },
        { label: document.name },
      ]}
      actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => handleDownload()}>
            {t('common:actions.download')}
          </Button>
          <Button variant="primary" onClick={handleUploadNewVersion}>
            Upload New Version
          </Button>
        </div>
      }
    >
      {/* Expiry Warning */}
      {isExpiringSoon(document) && (
        <Alert type="warning" className="mb-6">
          This document will expire on {formatDate(document.expiryDate!)}. Please renew or update it soon.
        </Alert>
      )}

      {/* Document expired */}
      {document.status === 'EXPIRED' && (
        <Alert type="error" className="mb-6">
          This document has expired. Please upload a new version or renew the document.
        </Alert>
      )}

      {/* Unverified warning */}
      {!document.verified && (
        <Alert type="info" className="mb-6">
          This document is pending verification by the certification body.
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Document Preview */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Document Preview</h3>
            </CardHeader>
            <CardBody>
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <div className="mb-4 flex justify-center">
                  <FileText className="w-16 h-16 text-neutral-400" />
                </div>
                <p className="text-gray-600 mb-4">{document.fileName}</p>
                <p className="text-sm text-gray-500 mb-4">
                  Preview not available in Sprint 1. Click download to view the document.
                </p>
                <Button variant="primary" onClick={() => handleDownload()}>
                  {t('common:actions.download')} ({formatFileSize(document.fileSize)})
                </Button>
              </div>
            </CardBody>
          </Card>

          {/* Description */}
          {document.description && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold">Description</h3>
              </CardHeader>
              <CardBody>
                <p className="text-gray-700">{document.description}</p>
              </CardBody>
            </Card>
          )}

          {/* Version History */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Version History</h3>
              <p className="text-sm text-gray-600 mt-1">
                {document.versions.length} version{document.versions.length > 1 ? 's' : ''}
              </p>
            </CardHeader>
            <CardBody>
              <Table
                columns={[
                  {
                    key: 'version',
                    header: 'Version',
                    render: (ver: DocumentVersion) => (
                      <div className="flex items-center gap-2">
                        <span className="font-medium">v{ver.version}</span>
                        {ver.version === document.currentVersion && (
                          <Badge variant="success" size="sm">
                            Current
                          </Badge>
                        )}
                      </div>
                    ),
                  },
                  {
                    key: 'uploadedAt',
                    header: 'Upload Date',
                    render: (ver: DocumentVersion) => (
                      <div className="text-sm">
                        <div>{formatDate(ver.uploadedAt)}</div>
                        <div className="text-xs text-gray-500">{formatDateTime(ver.uploadedAt)}</div>
                      </div>
                    ),
                  },
                  {
                    key: 'uploadedBy',
                    header: 'Uploaded By',
                    render: (ver: DocumentVersion) => (
                      <div className="text-sm">
                        <div className="font-medium">{ver.uploadedByName}</div>
                      </div>
                    ),
                  },
                  {
                    key: 'fileSize',
                    header: 'Size',
                    render: (ver: DocumentVersion) => (
                      <span className="text-sm">{formatFileSize(ver.fileSize)}</span>
                    ),
                  },
                  {
                    key: 'changes',
                    header: 'Changes',
                    render: (ver: DocumentVersion) => (
                      <span className="text-sm text-gray-600">{ver.changes || 'Initial upload'}</span>
                    ),
                  },
                  {
                    key: 'actions',
                    header: 'Actions',
                    render: (ver: DocumentVersion) => (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownload(ver.version)}
                      >
                        {t('common:actions.download')}
                      </Button>
                    ),
                  },
                ]}
                data={document.versions}
                keyExtractor={(ver) => ver.version}
              />
            </CardBody>
          </Card>
        </div>

        {/* Sidebar - Right Column */}
        <div className="space-y-6">
          {/* Document Information */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Information</h3>
            </CardHeader>
            <CardBody>
              <dl className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="mt-1">{getStatusBadge(document.status)}</dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">Category</dt>
                  <dd className="mt-1">
                    <Badge variant="default">{document.category}</Badge>
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">Current Version</dt>
                  <dd className="mt-1 text-sm font-medium">v{document.currentVersion}</dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">File Name</dt>
                  <dd className="mt-1 text-sm break-all">{document.fileName}</dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">File Size</dt>
                  <dd className="mt-1 text-sm">{formatFileSize(document.fileSize)}</dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">Upload Date</dt>
                  <dd className="mt-1 text-sm">{formatDate(document.uploadedAt)}</dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">Uploaded By</dt>
                  <dd className="mt-1 text-sm">{document.uploadedByName}</dd>
                </div>

                {document.expiryDate && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Expiry Date</dt>
                    <dd className={`mt-1 text-sm ${isExpiringSoon(document) ? 'text-orange-600 font-medium' : ''}`}>
                      {formatDate(document.expiryDate)}
                      {isExpiringSoon(document) && (
                        <div className="text-xs text-orange-600">
                          ({Math.ceil((new Date(document.expiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))} days remaining)
                        </div>
                      )}
                    </dd>
                  </div>
                )}

                <div>
                  <dt className="text-sm font-medium text-gray-500">Verification</dt>
                  <dd className="mt-1">
                    {document.verified ? (
                      <div>
                        <Badge variant="success" size="sm">
                          {t('common:status.verified')}
                        </Badge>
                        {document.verifiedBy && (
                          <div className="text-xs text-gray-500 mt-1">
                            By {document.verifiedByName} on {formatDate(document.verifiedAt!)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <Badge variant="info" size="sm">
                        {t('common:status.unverified')}
                      </Badge>
                    )}
                  </dd>
                </div>
              </dl>
            </CardBody>
          </Card>

          {/* Actions Card */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Actions</h3>
            </CardHeader>
            <CardBody>
              <div className="space-y-2">
                <Button variant="outline" fullWidth onClick={() => handleDownload()}>
                  {t('common:actions.download')}
                </Button>
                <Button variant="outline" fullWidth onClick={handleUploadNewVersion}>
                  Upload New Version
                </Button>
                <Button variant="outline" fullWidth onClick={() => alert('Edit metadata')}>
                  {t('common:actions.edit')} Metadata
                </Button>
                <Button variant="outline" fullWidth onClick={handleDelete} className="text-red-600 hover:text-red-700">
                  {t('common:actions.delete')}
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </PageLayout>
  );
};

export default DocumentDetailPage;

